/**
 * Quest dialogue reconciliation (`q-o071-playtest-fixes` (a), owner playtest O-071) — the
 * pure half of keeping the Quests panel's NPC dialogue true after the conversation opened.
 *
 * `npcDialogue` is a reply to ONE `talkIntent`: its offers and turn-ins are what was true at
 * that moment. The panel kept showing it while the quest log moved on, so a quest finished
 * with Wren's dialogue still open never grew a "turn in" button (the stale "accept" was the
 * only button left — and it does nothing for a held quest), while a quest already turned in
 * kept a dead "turn in". Only a second talk refreshed it — the owner's "Talking to the
 * questgiver does". Every `questLog` push now re-derives both lists from the log with the
 * server's own rule (quests.ts `handleTalk`: status `complete` and turn-in NPC == this NPC).
 *
 * It decides NOTHING the server does not re-check: `handleTurnIn` still gates on the NPC,
 * range and status. Pure; node-testable.
 */

import type {
  NpcDialoguePayload,
  QuestLogPayload,
  QuestRewardsGrantedWire,
  QuestTurnedInPayload,
} from '@great-cloud/shared/client';
import type { TalkLike, VoiceChoice } from '../../game/audio/voice.js';
import { choiceText } from '../../game/audio/voice.js';

/**
 * The dialogue as the quest log now says it should read: offers the player has since
 * accepted (held `active`/`complete`) drop out, and the turn-ins are exactly this NPC's
 * completed quests. Lines, greeting and still-open offers are untouched. Returns the SAME
 * object when nothing changed, so the store can skip a re-render.
 */
export function reconcileDialogue(
  dialogue: NpcDialoguePayload,
  log: QuestLogPayload,
): NpcDialoguePayload {
  const held = new Set(
    log.quests
      .filter((q) => q.status === 'active' || q.status === 'complete')
      .map((q) => q.questId),
  );
  const offers = dialogue.offers.filter((o) => !held.has(o.questId));
  const turnIns = log.quests
    .filter((q) => q.status === 'complete' && q.turnInNpc === dialogue.npcId)
    .map((q) => q.questId);
  const sameTurnIns =
    turnIns.length === dialogue.turnIns.length &&
    turnIns.every((id, i) => dialogue.turnIns[i] === id);
  if (offers.length === dialogue.offers.length && sameTurnIns) return dialogue;
  return { ...dialogue, offers, turnIns };
}

/** A "tell me about…" option as the panel draws it: the question and the lines it answers with. */
export interface TopicView {
  id: string;
  ask: string;
  lines: string[];
}

/**
 * What the dialogue box shows up front (q-npc-lore-talk-option, Owner Desk #279): the lines NO
 * topic owns (an NPC with no topics shows every line, as before), and each topic as an option
 * whose lines show only once the player asks. Out-of-range indexes are dropped (validate:content
 * refuses them; an older server simply sends no topics).
 */
export function dialogueView(d: Pick<NpcDialoguePayload, 'lines' | 'topics'>): {
  freeLines: string[];
  topics: TopicView[];
} {
  const owned = new Set<number>();
  const topics: TopicView[] = [];
  for (const t of d.topics ?? []) {
    const lines: string[] = [];
    for (const i of t.lines) {
      owned.add(i);
      const text = d.lines[i];
      if (text !== undefined) lines.push(text);
    }
    if (lines.length > 0) topics.push({ id: t.id, ask: t.ask, lines });
  }
  return { freeLines: d.lines.filter((_, i) => !owned.has(i)), topics };
}

/** Quest names that moved to `turned_in` between two logs — the turn-in's only feedback. */
export function newlyTurnedIn(prev: QuestLogPayload | null, next: QuestLogPayload): string[] {
  const before = new Map((prev?.quests ?? []).map((q) => [q.questId, q.status]));
  return next.quests
    .filter(
      (q) =>
        q.status === 'turned_in' && before.has(q.questId) && before.get(q.questId) !== 'turned_in',
    )
    .map((q) => q.name);
}

/**
 * Where a conversation stands after the player's choices (AD-870): the quest asked about (its
 * pitch shows under it with Accept / Decline), the topic asked (its lines show), and the NPC's
 * reply to the last answer-type choice (shown as the subtitle of the line it speaks) with the quest
 * it answers (`saidFor`; null for the farewell).
 */
export interface ConversationState {
  askedQuest: string | null;
  askedTopic: string | null;
  said: string | null;
  saidFor: string | null;
}

export const FRESH_CONVERSATION: ConversationState = {
  askedQuest: null,
  askedTopic: null,
  said: null,
  saidFor: null,
};

/**
 * The conversation after one choice. Asking about a quest reveals its pitch and the Accept /
 * Decline choice; asking a topic reveals its lines; every other choice closes those and shows the
 * NPC's reply line (null when the content has none). Pure: the panel renders it, voice.ts speaks
 * the same choice (`voiceChoice`), so what is heard is what is shown.
 */
export function afterChoice(d: TalkLike, choice: VoiceChoice): ConversationState {
  if (choice.kind === 'offer')
    return { askedQuest: choice.questId, askedTopic: null, said: null, saidFor: null };
  if (choice.kind === 'topic')
    return { askedQuest: null, askedTopic: choice.topicId, said: null, saidFor: null };
  return {
    askedQuest: null,
    askedTopic: null,
    said: choiceText(d, choice),
    saidFor: choice.kind === 'farewell' ? null : choice.questId,
  };
}

/**
 * Where the NPC's reply is drawn (q-demo-dialogue-playtest-2026-10-29): under the row of the quest
 * it answers when that row is on screen (`rows`: the quest ids the panel draws a row for: offers,
 * in-progress, turn-ins), so the answer appears where the player tapped, the way a topic's lines
 * and a quest's pitch do. The owner's "how goes it does nothing": the reply replaced the previous
 * one in a slot under the greeting, away from the button, and the line has no recording yet, so
 * the tap looked and sounded like nothing. Returns the row id, or null = the slot under the
 * greeting (a reply whose quest has no row any more, or a reply to no quest). Null when nothing
 * is said.
 */
export function replyRow(convo: ConversationState, rows: readonly string[]): string | null {
  if (convo.said === null || convo.saidFor === null) return null;
  return rows.includes(convo.saidFor) ? convo.saidFor : null;
}

/**
 * Quests the player holds from this NPC that are not finished (status `active`, turned in here)
 * and that it has a progress line for: each is an "ask how it goes" choice (AD-870).
 */
export function inProgressHere(
  d: Pick<NpcDialoguePayload, 'npcId' | 'questLines'>,
  log: QuestLogPayload | null,
): { questId: string; name: string }[] {
  return (log?.quests ?? [])
    .filter(
      (q) =>
        q.status === 'active' &&
        q.turnInNpc === d.npcId &&
        d.questLines?.[q.questId]?.progress !== undefined,
    )
    .map((q) => ({ questId: q.questId, name: q.name }));
}

/** Display names the completion card needs (the client catalogue's, injected so this stays pure). */
export interface RewardNames {
  item: (itemId: string) => string;
  faction: (factionId: string) => string;
}

const count = (n: number): string => n.toLocaleString('en-GB');

/**
 * One formatter per reward kind the server grants (`QuestRewardsGrantedWire`). Typed as a mapped
 * type over the wire, so a reward kind added to the wire does not compile without its line here
 * (AD-867's pattern): the completion card can never silently drop a reward the player received.
 */
const REWARD_LINES: {
  [K in keyof QuestRewardsGrantedWire]: (
    value: QuestRewardsGrantedWire[K],
    names: RewardNames,
  ) => string[];
} = {
  xp: (xp) => (xp > 0 ? [`${count(xp)} XP`] : []),
  shards: (shards) => (shards > 0 ? [`${count(shards)} Shards`] : []),
  items: (items, names) =>
    items
      .filter((i) => i.quantity > 0)
      .map((i) =>
        i.quantity > 1 ? `${names.item(i.itemId)} ×${i.quantity}` : names.item(i.itemId),
      ),
  reputation: (rep, names) =>
    rep
      .filter((r) => r.amount !== 0)
      .map(
        (r) =>
          `${r.amount > 0 ? '+' : ''}${count(r.amount)} reputation with ${names.faction(r.faction)}`,
      ),
};

/** The reward kinds, in the order the card lists them. */
export const REWARD_KINDS = Object.keys(REWARD_LINES) as (keyof QuestRewardsGrantedWire)[];

/** The card's reward lines: what the turn-in granted, one line per reward (empty = no reward). */
export function completionRewardLines(
  rewards: QuestRewardsGrantedWire,
  names: RewardNames,
): string[] {
  return REWARD_KINDS.flatMap((k) =>
    (REWARD_LINES[k] as (v: unknown, n: RewardNames) => string[])(rewards[k], names),
  );
}

/** The completion card as the HUD draws it (q-demo-dialogue-playtest-2026-10-29, AD-870 Am.1). */
export interface CompletionCardView {
  questId: string;
  title: string;
  name: string;
  /** The NPC's `complete` line (spoken, or its subtitle shown until it is recorded). */
  line: string | null;
  rewards: string[];
}

/** Generic for every quest: its name, the NPC's line and what the server says it granted. */
export function completionCard(p: QuestTurnedInPayload, names: RewardNames): CompletionCardView {
  return {
    questId: p.questId,
    title: 'Quest complete',
    name: p.name,
    line: p.completeText,
    rewards: completionRewardLines(p.rewards, names),
  };
}

/** The confirmed turn-in as a conversation the voice layer can answer (`complete:<questId>`). */
export function completionTalk(p: QuestTurnedInPayload): TalkLike {
  return {
    npcId: p.npcId,
    greeting: null,
    lines: [],
    offers: [],
    questLines: p.completeText ? { [p.questId]: { complete: p.completeText } } : {},
  };
}
